import { DataPoint } from "regression";
import {
  createK,
  createQEFF2Data,
  createQH2Data,
  createQNPSH2Data,
  findIntersectionPoint,
  getNpshrByData,
  powerFormula,
} from "../../../utils";

// Engineering-grade public-reference implementation.
// This module intentionally follows a "HI-style" correction flow without
// claiming a strict ANSI/HI 9.6.7 formula-for-formula implementation.

interface ViscosityCorrectionContextInput {
  quantity: number;
  head: number;
  sg?: number;
  viscosityCanonical?: number;
  speed?: number;
  qh2: DataPoint[];
  qeff2: DataPoint[];
  qnpsh2?: DataPoint[];
}

interface DutyPointInput {
  quantity: number;
  head: number;
  eff: number;
  npsh?: number;
  sg?: number;
}

interface SolveViscousAnchoredCurveInput {
  quantity: number;
  head: number;
  sg?: number;
  viscosityCanonical?: number;
  speed?: number;
  type?: number;
  qhPoints: DataPoint[];
  qeffPoints: DataPoint[];
  qnpshPoints?: DataPoint[];
  qhRawPoints?: DataPoint[];
  qeffRawPoints?: DataPoint[];
  qhEquation?: number[];
  center?: number;
}

interface CorrectedDutyPointResult {
  eff: number;
  power: number;
  npsh?: number;
}

interface SolveViscousAnchoredCurveResult {
  k: number;
  waterDutyPoint: {
    quantity: number;
    head: number;
  };
  viscousDutyPoint: {
    quantity: number;
    head: number;
  };
  waterCurves: {
    qH2: DataPoint[];
    qEFF2: DataPoint[];
    qNPSH2: DataPoint[];
  };
  viscousCurves: {
    qH2v: DataPoint[];
    qEFF2v: DataPoint[];
    qNPSH2v: DataPoint[];
    qP2v: DataPoint[];
    qP2vTrusted: DataPoint[];
  };
  correctedDutyPointResult: CorrectedDutyPointResult;
  debug: {
    viscosityContext: ViscosityCorrectionContext;
    waterEquivalentDutyPoint: DataPoint;
    viscousHeadAtQ?: number;
    qH2Sample: DataPoint[];
    qH2vSample: DataPoint[];
    qEFF2Sample: DataPoint[];
    qEFF2vSample: DataPoint[];
    qP2vTrustedSample: DataPoint[];
    qP2vSample: DataPoint[];
  };
}

interface ViscosityCorrectionContext {
  quantity: number;
  head: number;
  sg: number;
  speed: number;
  viscosityCanonical: number;
  noCorrection: boolean;
  bepQuantity: number;
  bepHead: number;
  bParameter: number;
  cQ: number;
  cH: number;
  cEff: number;
  cNpshr: number;
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const sortByQuantity = (points: DataPoint[] = []) =>
  [...points]
    .filter((point) => Array.isArray(point) && Number.isFinite(point[0]) && Number.isFinite(point[1]))
    .sort((a, b) => a[0] - b[0]);

const interpolateY = (points: DataPoint[] = [], quantity: number) => {
  const sorted = sortByQuantity(points);
  if (!sorted.length) {
    return undefined;
  }

  if (quantity <= sorted[0][0]) {
    return sorted[0][1];
  }

  const last = sorted[sorted.length - 1];
  if (quantity >= last[0]) {
    return last[1];
  }

  for (let index = 0; index < sorted.length - 1; index += 1) {
    const current = sorted[index];
    const next = sorted[index + 1];

    if (quantity >= current[0] && quantity <= next[0]) {
      const deltaX = next[0] - current[0];
      if (deltaX === 0) {
        return current[1];
      }

      const ratio = (quantity - current[0]) / deltaX;
      return current[1] + (next[1] - current[1]) * ratio;
    }
  }

  return last[1];
};

const getNearestPoint = (points: DataPoint[] = [], quantity: number) => {
  if (!points.length) {
    return undefined;
  }

  return points.reduce((closest, current) => {
    const closestDelta = Math.abs(closest[0] - quantity);
    const currentDelta = Math.abs(current[0] - quantity);
    return currentDelta < closestDelta ? current : closest;
  }, points[0]);
};

const samplePoints = (points: DataPoint[] = [], size = 8) => {
  if (points.length <= size) {
    return points;
  }

  const result: DataPoint[] = [];
  const maxIndex = points.length - 1;

  for (let index = 0; index < size; index += 1) {
    result.push(points[Math.round((maxIndex * index) / (size - 1))]);
  }

  return result;
};

const getBepPoint = (points: DataPoint[] = []) => {
  if (!points.length) {
    return undefined;
  }

  return points.reduce((best, current) => {
    return current[1] > best[1] ? current : best;
  }, points[0]);
};

interface EngineeringViscosityInput {
  viscosityCanonical: number;
  bepQuantity: number;
  bepHead: number;
  speed: number;
}

interface EngineeringCorrectionFactors {
  noCorrection: boolean;
  bParameter: number;
  cQ: number;
  cH: number;
  cEff: number;
  cNpshr: number;
}

const buildEngineeringViscosityInput = (
  viscosityCanonical: number,
  bepQuantity: number,
  bepHead: number,
  speed: number
): EngineeringViscosityInput => ({
  viscosityCanonical,
  bepQuantity: Math.max(bepQuantity, 1),
  bepHead: Math.max(bepHead, 1),
  speed: Math.max(speed, 1),
});

const computeEngineeringBParameter = ({
  viscosityCanonical,
  bepQuantity,
  bepHead,
  speed,
}: EngineeringViscosityInput) =>
  16.5 *
  ((Math.pow(viscosityCanonical, 0.5) * Math.pow(bepHead, 0.0625)) /
    (Math.pow(bepQuantity, 0.375) * Math.pow(speed, 0.25)));

const resolveEngineeringCorrectionFactors = (
  input: EngineeringViscosityInput
): EngineeringCorrectionFactors => {
  const bParameter = computeEngineeringBParameter(input);

  if (input.viscosityCanonical <= 1.5 || bParameter <= 1) {
    return {
      noCorrection: true,
      bParameter,
      cQ: 1,
      cH: 1,
      cEff: 1,
      cNpshr: 1,
    };
  }

  const logB = Math.max(0, Math.log10(Math.max(bParameter, 1)));

  return {
    noCorrection: false,
    bParameter,
    cQ: clamp(Math.exp(-0.05 * Math.pow(logB, 1.55)), 0.72, 1),
    cH: clamp(Math.exp(-0.08 * Math.pow(logB, 1.35)), 0.62, 1),
    cEff: clamp(Math.exp(-0.18 * Math.pow(logB, 1.2)), 0.32, 1),
    cNpshr: clamp(1 + 0.22 * Math.pow(logB, 1.1), 1, 1.9),
  };
};

const buildEngineeringCorrectionFactors = (
  viscosityCanonical: number,
  bepQuantity: number,
  bepHead: number,
  speed: number
) =>
  resolveEngineeringCorrectionFactors(
    buildEngineeringViscosityInput(
      viscosityCanonical,
      bepQuantity,
      bepHead,
      speed
    )
  );

export const buildViscosityCorrectionContext = ({
  quantity,
  head,
  sg,
  viscosityCanonical,
  speed,
  qh2,
  qeff2,
}: ViscosityCorrectionContextInput): ViscosityCorrectionContext => {
  const bepEffPoint = getBepPoint(qeff2) || [quantity, 100];
  const bepQuantity = bepEffPoint[0];
  const bepHeadPoint = getNearestPoint(qh2, bepQuantity);
  const bepHead = bepHeadPoint ? bepHeadPoint[1] : head;
  const finalSg = sg && Number.isFinite(sg) && sg > 0 ? sg : 1;
  const finalSpeed = speed && Number.isFinite(speed) && speed > 0 ? speed : 2900;
  const finalViscosity =
    viscosityCanonical && Number.isFinite(viscosityCanonical) && viscosityCanonical > 0
      ? viscosityCanonical
      : 1;
  const factors = buildEngineeringCorrectionFactors(
    finalViscosity,
    Math.max(bepQuantity, 1),
    Math.max(bepHead, 1),
    finalSpeed
  );

  return {
    quantity,
    head,
    sg: finalSg,
    speed: finalSpeed,
    viscosityCanonical: finalViscosity,
    bepQuantity,
    bepHead,
    ...factors,
  };
};

const getFlowRatio = (quantity: number, baseQuantity: number) =>
  clamp(quantity / Math.max(baseQuantity, 1e-6), 0, 1.8);

const getEngineeringHeadScale = (
  ratio: number,
  context: ViscosityCorrectionContext
) => {
  const normalizedRatio = clamp(ratio, 0, 1.4);

  if (normalizedRatio <= 1) {
    return 1 - (1 - context.cH) * Math.pow(normalizedRatio, 0.75);
  }

  return clamp(
    context.cH - (1 - context.cH) * 0.2 * (normalizedRatio - 1),
    context.cH * 0.9,
    1
  );
};

const buildWaterEquivalentDutyPoint = (
  quantity: number,
  head: number,
  context: ViscosityCorrectionContext
): DataPoint => {
  if (context.noCorrection) {
    return [quantity, head] as DataPoint;
  }

  const waterQuantity = quantity / Math.max(context.cQ, 1e-6);
  const ratio = getFlowRatio(waterQuantity, context.bepQuantity);
  const headScale = getEngineeringHeadScale(ratio, context);
  const waterHead = head / Math.max(headScale, 1e-6);

  return [waterQuantity, waterHead] as DataPoint;
};

export const correctQH2ByViscosity = (
  points: DataPoint[] = [],
  context: ViscosityCorrectionContext
): DataPoint[] => {
  if (context.noCorrection) {
    return points;
  }

  return points.map(([q, h]) => {
    const ratio = getFlowRatio(q, context.bepQuantity);
    const headScale = getEngineeringHeadScale(ratio, context);

    return [
      q * context.cQ,
      Math.max(0, h * headScale),
    ] as DataPoint;
  });
};

export const correctQEFF2ByViscosity = (
  points: DataPoint[] = [],
  context: ViscosityCorrectionContext
): DataPoint[] => {
  if (context.noCorrection) {
    return points;
  }

  return points.map(([q, eff]) => {
    const ratio = getFlowRatio(q, context.bepQuantity);
    const spreadPenalty = 1 - 0.06 * Math.min(Math.abs(ratio - 1), 1);
    const finalFactor = clamp(context.cEff * spreadPenalty, 0.2, 1);

    return [
      q * context.cQ,
      clamp(eff * finalFactor, 0, 100),
    ] as DataPoint;
  });
};

export const correctQNPSH2ByViscosity = (
  points: DataPoint[] = [],
  context: ViscosityCorrectionContext
): DataPoint[] => {
  if (context.noCorrection) {
    return points;
  }

  return points.map(([q, npsh]) => {
    const ratio = getFlowRatio(q, context.bepQuantity);
    const npshFactor =
      1 + (context.cNpshr - 1) * clamp(0.7 + 0.3 * ratio, 0.7, 1.2);

    return [
      q * context.cQ,
      Math.max(0, npsh * npshFactor),
    ] as DataPoint;
  });
};

export const createCorrectedPowerCurve = (
  qhPoints: DataPoint[] = [],
  qeffPoints: DataPoint[] = [],
  sg: number = 1
) => {
  const finalSg = sg && Number.isFinite(sg) && sg > 0 ? sg : 1;

  return qhPoints.map((point, index) => {
    const effPoint = qeffPoints[index] || qeffPoints[qeffPoints.length - 1];
    const efficiency = effPoint ? effPoint[1] / 100 : 0;

    if (!efficiency || point[0] <= 0 || point[1] <= 0) {
      return [point[0], 0] as DataPoint;
    }

    return [point[0], ((point[0] * point[1]) / 367.2 / efficiency) * finalSg] as DataPoint;
  });
};

const evaluateViscousCandidate = (
  k: number,
  {
    quantity,
    head,
    sg,
    viscosityCanonical,
    speed,
    type = 0,
    qhPoints,
    qeffPoints,
    qnpshPoints = [],
    qhRawPoints,
    qeffRawPoints,
  }: SolveViscousAnchoredCurveInput
) => {
  const qH2 = createQH2Data(k, qhPoints, type);
  const qEFF2 = createQEFF2Data(k, qeffPoints, type);
  const qNPSH2 = createQNPSH2Data(k, qnpshPoints, type);
  const qH2Raw = qhRawPoints?.length ? createQH2Data(k, qhRawPoints, type) : qH2;
  const qEFF2Raw = qeffRawPoints?.length ? createQEFF2Data(k, qeffRawPoints, type) : qEFF2;

  const context = buildViscosityCorrectionContext({
    quantity,
    head,
    sg,
    viscosityCanonical,
    speed,
    qh2: qH2,
    qeff2: qEFF2,
    qnpsh2: qNPSH2,
  });

  const qH2v = correctQH2ByViscosity(qH2, context);
  const qEFF2v = correctQEFF2ByViscosity(qEFF2, context);
  const qNPSH2v = correctQNPSH2ByViscosity(qNPSH2, context);
  const qP2v = createCorrectedPowerCurve(qH2v, qEFF2v, sg || 1);
  const qH2vRaw = correctQH2ByViscosity(qH2Raw, context);
  const qEFF2vRaw = correctQEFF2ByViscosity(qEFF2Raw, context);
  const qP2vTrusted = createCorrectedPowerCurve(qH2vRaw, qEFF2vRaw, sg || 1);

  const viscousHeadAtQ = interpolateY(qH2v, quantity);
  const viscousEffAtQ = interpolateY(qEFF2v, quantity) ?? 0;
  const viscousNpshAtQ = qNPSH2v.length ? getNpshrByData(qNPSH2v, quantity) : undefined;
  const correctedDutyPointResult = {
    eff: clamp(viscousEffAtQ, 0, 100),
    power:
      viscousEffAtQ > 0
        ? powerFormula([quantity, head], viscousEffAtQ / 100) * (sg || 1)
        : 0,
    npsh: viscousNpshAtQ,
  };

  return {
    error:
      viscousHeadAtQ === undefined
        ? Number.POSITIVE_INFINITY
        : viscousHeadAtQ - head,
    waterCurves: {
      qH2,
      qEFF2,
      qNPSH2,
    },
    viscousCurves: {
      qH2v,
      qEFF2v,
      qNPSH2v,
      qP2v,
      qP2vTrusted,
    },
    correctedDutyPointResult,
    debug: {
      viscosityContext: context,
      waterEquivalentDutyPoint: buildWaterEquivalentDutyPoint(quantity, head, context),
      viscousHeadAtQ,
      qH2Sample: samplePoints(qH2),
      qH2vSample: samplePoints(qH2v),
      qEFF2Sample: samplePoints(qEFF2),
      qEFF2vSample: samplePoints(qEFF2v),
      qP2vTrustedSample: samplePoints(qP2vTrusted),
      qP2vSample: samplePoints(qP2v),
    },
  };
};

export const solveViscousAnchoredCurve = (
  input: SolveViscousAnchoredCurveInput
): SolveViscousAnchoredCurveResult | null => {
  const {
    quantity,
    head,
    type = 0,
    qhEquation = [],
    center = quantity,
  } = input;

  if (!input.qhPoints?.length || !input.qeffPoints?.length) {
    return null;
  }

  const solveKFromDutyPoint = (dutyPoint: DataPoint) => {
    const intersectionPoint = qhEquation.length
      ? findIntersectionPoint(dutyPoint[0], dutyPoint[1], qhEquation, type, center)
      : dutyPoint;

    if (!intersectionPoint) {
      return null;
    }

    const baseIntersectionQuantity = Math.max(intersectionPoint[0], 0.0001);
    const nextK = createK(dutyPoint[0], baseIntersectionQuantity, type);

    return Number.isFinite(nextK) && nextK > 0 ? nextK : null;
  };

  let waterEquivalentDutyPoint = [quantity, head] as DataPoint;
  let currentK = solveKFromDutyPoint(waterEquivalentDutyPoint);

  if (!currentK) {
    return null;
  }

  let best = {
    k: currentK,
    ...evaluateViscousCandidate(currentK, input),
  };

  for (let index = 0; index < 8; index += 1) {
    waterEquivalentDutyPoint = buildWaterEquivalentDutyPoint(
      quantity,
      head,
      best.debug.viscosityContext
    );

    const nextK = solveKFromDutyPoint(waterEquivalentDutyPoint);

    if (!nextK || Math.abs(nextK - currentK) < 1e-6) {
      break;
    }

    currentK = nextK;
    best = {
      k: currentK,
      ...evaluateViscousCandidate(currentK, input),
    };
  }

  return {
    k: best.k,
    waterDutyPoint: {
      quantity: waterEquivalentDutyPoint[0],
      head: waterEquivalentDutyPoint[1],
    },
    viscousDutyPoint: {
      quantity,
      head,
    },
    waterCurves: best.waterCurves,
    viscousCurves: best.viscousCurves,
    correctedDutyPointResult: best.correctedDutyPointResult,
    debug: best.debug,
  };
};

export const correctDutyPointByViscosity = (
  result: DutyPointInput,
  context: ViscosityCorrectionContext
) => {
  if (context.noCorrection) {
    return {
      eff: result.eff,
      power:
        ((result.quantity * result.head) / 367.2 / (Math.max(result.eff, 0.0001) / 100)) *
        (result.sg || context.sg || 1),
      npsh: result.npsh,
    };
  }

  const ratio = getFlowRatio(result.quantity, context.bepQuantity);
  const spreadPenalty = 1 - 0.06 * Math.min(Math.abs(ratio - 1), 1);
  const eff = clamp(result.eff * context.cEff * spreadPenalty, 0, 100);
  const sg = result.sg || context.sg || 1;
  const power =
    eff > 0
      ? ((result.quantity * result.head) / 367.2 / (eff / 100)) * sg
      : 0;
  const npsh =
    result.npsh === undefined
      ? result.npsh
      : Math.max(
          0,
          result.npsh *
            (1 + (context.cNpshr - 1) * clamp(0.7 + 0.3 * ratio, 0.7, 1.2))
        );

  return {
    eff,
    power,
    npsh,
  };
};
